<template>
  <validation-observer
    v-if="needsPassword && hasValidUserData"
    v-show="isRenderAfterFullfilled"
    v-slot="observer"
    slim
  >
    <form-section
      :id="questionId || 'user-data'"
      :no-validity="existingUserForEmail && isUserLoggedIn"
      :validated="formObserver.validated"
      v-bind="observer"
    >
      <form-row
        v-if="!isUserLoggedIn"
        :label="formRowInfo.label"
        :help-text="formRowInfo.helpText"
        :is-vertical-splitted="false"
        :error="hasErrors(observer)"
      >
        <validation-provider
          v-if="showEmailField"
          v-slot="{ errors, valid }"
          rules="email"
          name="login-email"
          :vid="`${stepId}_user-login-email`"
        >
          <qz-text-field
            v-model="emailComputed"
            :data-test="fieldTrackerId"
            :name="`${stepId}_user-login-email`"
            type="email"
            autocomplete="email"
            placeholder="Email"
            :error="!!suggestedEmail"
            :error-messages="errors"
            :maxlength="inputCharsLimiters.EMAIL"
            @change="handleEmailChange(valid)"
            @keyup="helpers.limitInputChars(questionData, 'email', inputCharsLimiters.EMAIL)"
          />
          <div
            v-if="suggestedEmail && !errors.length"
            class="mt-2 ml-3 text-caption"
          >
            <a
              class="red--text text-decoration-underline"
              @click.prevent="applySuggestion"
            >
              Did you mean: {{ suggestedEmail }}?
            </a>
          </div>
        </validation-provider>
      </form-row>

      <!--  'create password' input if account with given user's data does not exist  -->
      <new-account-password
        v-if="!existingUserForEmail"
        :has-errors="hasErrors(observer)"
        :step-id="stepId"
        :step-data="stepData"
        @user-data-changed="newUserData => questionData.userData = newUserData"
        @user-data-confirm-changed="newUserDataConfirm => questionData._userDataConfirm = newUserDataConfirm"
        @user-data-keyup="helpers.limitInputChars(stepData, 'userData', constants.PASSWORD_MAX)"
        @user-data-confirm-keyup="helpers.limitInputChars(stepData, '_userDataConfirm', constants.PASSWORD_MAX)"
      />

      <!--  default 'log in password' input   -->
      <new-user-password
        v-else-if="!isUserLoggedIn"
        :has-errors="hasErrors(observer)"
        :step-id="stepId"
        :email="emailComputed"
        :is-quick-pass-login="isQuickPassLogin"
        :field-errors="fieldErrors"
        :partner="config.partner"
        @reset-question="resetQuestion"
        @emailed-one-time-password="isQuickPassLogin = true"
        @login="login"
        @login-with-password="passLogin"
        @password-changed="newPassword => loginPassword = newPassword"
        @passcode-changed="newPasscode => loginPasscode = newPasscode"
      />

      <box v-else>
        <icon
          slot="icon"
          size="56"
          :icon="helpers.getAdditionalIcon(userTitle)"
        />
        <p> {{ loggedInText }} </p>
      </box>
      <user-keep-sign-in-small
        v-if="isQZCarCommunicationOptIn"
        question-id="user-keep-sign-in"
        :product-name="productName"
        :step-id="stepId"
        :form-observer="formObserver"
        :form-data="formData"
        :step-data="stepData"
        :default-values="{
          userKeepSignIn: true
        }"
        @input="questionData.userKeepSignIn = $event.userKeepSignIn"
      />
    </form-section>
  </validation-observer>
</template>

<script>
import {
  throttle,
  intersection,
  snakeCase,
} from 'lodash';
import { validate } from 'vee-validate';
import Cookies from 'js-cookie';
import QuestionBase from './Base';
import NewAccountPassword from './password/NewAccountPassword';
import NewUserPassword from './password/NewUserPassword';
import UserKeepSignInSmall from './password/UserKeepSignInSmall';
import { inject } from '@vue/composition-api';

export default {
  name: 'question-password',
  extends: QuestionBase,
  components: {
    NewAccountPassword,
    NewUserPassword,
    UserKeepSignInSmall,
  },
  data () {
    return {
      fields: [
        'userData',
        '_userDataConfirm',
      ],
      isPasswordOnSameStep: null,
      loginPassword: '',
      loginPasscode: '',
      email: '',
      isQuickPassLogin: false,
      isUserLoggedIn: false,
      existingUserForEmail: false,
      hasValidUserData: false,
      showEmailField: false,
      fieldErrors: [],
    };
  },
  async created () {
    if (this.needsPassword) {
      this.$emit('loading', true);
      // if the password section located on the same step
      // with data that are used for api call
      this.isPasswordOnSameStep = this.checkStepHasFields();

      if (this.isPasswordOnSameStep) {
        this.fields.push(this.emailField);
        this.$set(this.questionData, this.emailField, this.stepData.email);

        await this.watchPasswordData();
      } else {
        await this.initQuotePassword();
        this.email = this.formData.drivers[0].email;
      }

      this.$emit('loading', false);
    }
    if (this.isQZCarCommunicationOptIn) {
      this.fields.push('userKeepSignIn');
    }
  },
  computed: {
    emailComputed: {
      get () {
        return this.isPasswordOnSameStep ? this.stepData.email : this.email;
      },
      set (val) {
        if (this.isPasswordOnSameStep) {
          this.questionData[this.emailField] = val;
          return;
        }

        this.email = val;
      },
    },
    emailField () {
      return this.questionFields.email || 'email';
    },
    needsPassword () {
      return this.config.partnerSignin;
    },
    userTitle () {
      return this.isPasswordOnSameStep ? this.stepData.title : this.formData.drivers[0].title;
    },
    loggedInText () {
      return this.isUserLoggedIn
        ? 'Welcome back! We will add your quote results to your account so you can easily retrieve them later.'
        : 'Good job you are now logged in. Your quote results will be added to your account so you can easily retrieve them later.';
    },
    formRowInfo () {
      if (this.existingUserForEmail) {
        return {
          label: 'Welcome Back! It looks like you already have an account with us.',
          helpText: 'Please login or enter a new email address to continue.',
        };
      }

      return {
        label: 'It looks like this is your first time here. Hello, nice to meet you!',
        helpText: 'Please create a password so that you can come back anytime to retrieve/modify your quote on any device.',
      };
    },
    suggestedEmail () {
      return this.helpers.suggestedEmail(this.emailComputed);
    },
    fieldTrackerId () {
      return `${snakeCase(this.questionFields.email)}_login`;
    },
  },
  methods: {
    handleEmailChange (valid) {
      if (valid && !this.isPasswordOnSameStep) {
        this.initQuotePassword();
      }
    },
    async initQuotePassword () {
      const {
        firstName,
        surname: lastName,
        email,
        dateOfBirth: birthDate,
      } = this.isPasswordOnSameStep ? this.stepData : this.formData.drivers[0];
      try {
        const quoteRef = Cookies.get('quote_ref');
        const calculatedQuoteRef =
          quoteRef && this.helpers.isQzOrCompareNiPartner(this.config.partner)
            ? quoteRef
            : null;

        const { status, data } = await this.$api.authUserQuote({
          firstName,
          lastName,
          email: this.emailComputed || email,
          birthDate,
          partner: this.config.partner,
          userKeepSignInVal: calculatedQuoteRef,
        });

        if (status !== 'success') {
          this.resetQuestion();
          return;
        }

        const { existingUserForEmail, userId, existingUser } = data;
        this.existingUserForEmail = existingUserForEmail;

        if (!existingUserForEmail && !existingUser) {
          this.isUserLoggedIn = false;
        }

        if (existingUserForEmail) {
          await this.$nextTick();
          this.isUserLoggedIn = !!(userId);
          this.showEmailField = !userId;
          this.resetQuestion();
        }

        this.hasValidUserData = true;
      } catch (error) {
        this.resetQuestion();
        console.error(error);
      }
    },
    async watchPasswordData () {
      this.$watch(
        vm => {
          const { firstName, surname, dateOfBirth, email } = vm.stepData;
          return [firstName, surname, dateOfBirth, email];
        },
        throttle(async ([firstName, surname, dateOfBirth, email]) => {
          if (!(firstName && surname && dateOfBirth && email)) {
            this.hasValidUserData = false;
            return;
          }

          const validationResult = await Promise.all([
            validate(firstName, 'name'),
            validate(surname, 'name'),
            validate(dateOfBirth, 'dateOfBirth'),
            validate(email, 'email'),
          ]);

          if (!validationResult.every((result) => result.valid)) return;

          await this.initQuotePassword();
        }, 300, { trailing: true, leading: false }),
        {
          immediate: true,
        },
      );
    },
    checkStepHasFields () {
      const requiredFields = [
        'firstName',
        'surname',
        'email',
        'dateOfBirth',
      ];

      return intersection(requiredFields, Object.keys(this.stepData)).length ===
        requiredFields.length;
    },
    resetQuestion () {
      this.$delete(this.questionData, 'userData');
      this.$delete(this.questionData, '_userDataConfirm');
      this.loginPasscode = '';
      this.loginPassword = '';
    },
    async login () {
      const data = {
        email: this.emailComputed,
        partner: this.config.partner,
      };

      if (this.isQuickPassLogin) {
        data.passcode = this.loginPasscode;
      } else {
        data.password = this.loginPassword;
      }

      try {
        const { status, errors } = await this.$api.userLogin(data);

        if (status === 'success') {
          this.isUserLoggedIn = true;
        }

        if (status === 'error' && errors) {
          this.fieldErrors = errors;
        }
      } catch (error) {
        console.log(error);
      }
    },
    passLogin () {
      this.resetQuestion();
      this.isQuickPassLogin = false;
    },
    hasErrors (observer) {
      return this.formObserver.validated && observer.failed;
    },
    applySuggestion () {
      this.emailComputed = this.suggestedEmail;
    },
  },
  setup () {
    const { isQZCarCommunicationOptIn } = inject('abTastyControllerUI');

    return {
      isQZCarCommunicationOptIn,
    };
  },
};
</script>

<style lang='scss' scoped>
.mt-gap {
  margin-top: var(--gap, 24px);
}
</style>
